import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Chip,
  Box,
  Tooltip,
  Divider,
  alpha,
} from '@mui/material';
import {
  People as PeopleIcon,
  GridOn as GridIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { Board, TeamMapping } from '../types';
import { sportsApi } from '../api';
import { formatGameDateTime, formatCardCountdown } from '../utils/dateFormatting';
import { PlayerAvatar } from './shared/PlayerAvatar';
import { TeamLogo } from './shared/TeamLogo';
import { NumberSetBadge } from './NumberSetBadge';

interface BoardCardProps {
  board: Board;
  openSquares?: number;
}

// Get quarter labels based on number of periods
const getQuarterLabels = (quarters: number): { short: string; full: string }[] => {
  if (quarters === 2) {
    return [
      { short: '1H', full: '1st Half' },
      { short: '2H', full: '2nd Half' },
    ];
  }
  if (quarters === 3) {
    return [
      { short: 'P1', full: '1st Period' },
      { short: 'P2', full: '2nd Period' },
      { short: 'P3', full: '3rd Period' },
    ];
  }
  // Default 4 quarters
  return [
    { short: 'Q1', full: '1st Quarter' },
    { short: 'Q2', full: '2nd Quarter' },
    { short: 'Q3', full: '3rd Quarter' },
    { short: 'Q4', full: '4th Quarter' },
  ];
};

// Get game progress info based on last completed quarter
const getGameProgress = (lastCompletedQuarter: number | null | undefined, quarters: number) => {
  const labels = getQuarterLabels(quarters);

  if (lastCompletedQuarter === null || lastCompletedQuarter === undefined) {
    return null; // No game progress yet
  }

  // Handle Final/OT (quarter 0 means Final in OT-enabled games)
  if (lastCompletedQuarter === 0) {
    return {
      label: 'Final',
      tooltip: 'Final score recorded',
      isComplete: true,
    };
  }

  const quarterIndex = lastCompletedQuarter - 1;

  if (quarterIndex >= labels.length - 1) {
    return {
      label: 'Final',
      tooltip: `${labels[labels.length - 1].full} score recorded`,
      isComplete: true,
    };
  }

  const completedLabel = labels[quarterIndex];
  const nextLabel = labels[quarterIndex + 1];

  return {
    label: nextLabel.short,
    tooltip: `${completedLabel.full} complete, awaiting ${nextLabel.full}`,
    isComplete: false,
  };
};

export const BoardCard: React.FC<BoardCardProps> = ({ board, openSquares }) => {
  const navigate = useNavigate();
  const isCompleted = board.status === 'completed';
  const isLiveGame = !!board.externalGameId;

  // Fetch team mappings for live game boards (logos, colors)
  const [teamMappings, setTeamMappings] = useState<TeamMapping[]>([]);
  useEffect(() => {
    if (!board.externalGameId) return;
    let mounted = true;
    sportsApi.getGame(board.externalGameId).then(data => {
      if (mounted) setTeamMappings(data.teamMappings || []);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [board.externalGameId]);

  // Countdown timer for upcoming live games
  const [countdown, setCountdown] = useState(() => {
    if (!board.externalGameId || !board.gameDate) return '';
    const remaining = new Date(board.gameDate).getTime() - Date.now();
    return remaining > 0 ? formatCardCountdown(remaining) : '';
  });
  useEffect(() => {
    if (!isLiveGame || !board.gameDate) return;
    const gameTime = new Date(board.gameDate).getTime();
    const update = () => {
      const remaining = gameTime - Date.now();
      setCountdown(remaining > 0 ? formatCardCountdown(remaining) : '');
    };
    // Update every 30s for responsive countdown display
    const interval = setInterval(update, 30_000);
    return () => clearInterval(interval);
  }, [isLiveGame, board.gameDate]);

  // Get status chip props
  const getStatusChip = () => {
    switch (board.status) {
      case 'draft':
        return { label: 'Draft', color: 'default' as const, variant: 'outlined' as const };
      case 'live':
        return { label: 'Live', color: 'success' as const, variant: 'filled' as const };
      case 'locked':
        return { label: 'Locked', color: 'warning' as const, variant: 'filled' as const };
      case 'completed':
        return { label: 'Completed', color: 'info' as const, variant: 'filled' as const };
      default:
        return { label: 'Unknown', color: 'default' as const, variant: 'outlined' as const };
    }
  };

  const statusChip = getStatusChip();
  const onlinePlayerCount = board.participants?.length || 0;
  const offlinePlayerCount = board.offlinePlayerCount || 0;
  const totalPlayerCount = onlinePlayerCount + offlinePlayerCount;
  const gameProgress = getGameProgress(board.lastCompletedQuarter, board.quarters || 4);

  const awayTeamMapping = isLiveGame ? teamMappings.find(t => t.teamName === board.awayTeam || t.abbreviation === board.awayTeam) : null;
  const homeTeamMapping = isLiveGame ? teamMappings.find(t => t.teamName === board.homeTeam || t.abbreviation === board.homeTeam) : null;

  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      opacity: isCompleted ? 0.7 : 1,
      ...(isLiveGame && awayTeamMapping?.primaryColor && homeTeamMapping?.primaryColor && {
        borderTop: '3px solid',
        borderImage: `linear-gradient(90deg, ${awayTeamMapping.primaryColor} 0%, ${alpha(awayTeamMapping.primaryColor, 0.3)} 45%, ${alpha(homeTeamMapping.primaryColor, 0.3)} 55%, ${homeTeamMapping.primaryColor} 100%) 1`,
      }),
    }} data-testid="board-card">
      <CardActionArea
        onClick={() => navigate(`/boards/${board.slug || board.id}`)}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography
              variant="h6"
              component="div"
              noWrap
              sx={{ color: isCompleted ? 'text.secondary' : 'text.primary', flex: 1, mr: 1 }}
            >
              {board.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
              {/* Countdown for upcoming live games */}
              {isLiveGame && countdown && !gameProgress && (
                <Chip
                  label={countdown}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: 22,
                    bgcolor: 'grey.700',
                    color: '#fff',
                  }}
                />
              )}
              {/* Game Progress Indicator */}
              {gameProgress && (
                <Tooltip title={gameProgress.tooltip} arrow>
                  <Chip
                    label={gameProgress.label}
                    size="small"
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.75rem',
                      height: 24,
                      px: 0.5,
                      backgroundColor: gameProgress.isComplete ? '#2e7d32' : '#e65100',
                      color: '#fff',
                      boxShadow: gameProgress.isComplete ? 'none' : '0 0 8px rgba(230,81,0,0.5)',
                      animation: gameProgress.isComplete ? 'none' : 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.6 },
                      },
                    }}
                  />
                </Tooltip>
              )}
              <Chip
                label={statusChip.label}
                color={statusChip.color}
                variant={statusChip.variant}
                size="small"
              />
            </Box>
          </Box>

          {isLiveGame && teamMappings.length > 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <TeamLogo mapping={awayTeamMapping ?? undefined} alt={board.awayTeam} />
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {awayTeamMapping?.abbreviation || board.awayTeam}
              </Typography>
              <Typography color="text.disabled" sx={{ fontSize: '0.8rem', mx: 0.25 }}>@</Typography>
              <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {homeTeamMapping?.abbreviation || board.homeTeam}
              </Typography>
              <TeamLogo mapping={homeTeamMapping ?? undefined} alt={board.homeTeam} />
            </Box>
          ) : (
            <Typography color="text.secondary" gutterBottom>
              {board.awayTeam} @ {board.homeTeam}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary">
            {formatGameDateTime(board.gameDate)}
          </Typography>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={board.cellMode === 'quad' ? '25 Cells' : board.cellMode === 'paired' ? '50 Cells' : '100 Cells'}
              size="small"
              color={board.cellMode === 'quad' ? 'secondary' : board.cellMode === 'paired' ? 'primary' : 'default'}
            />
            <NumberSetBadge numberMode={board.numberMode} hideForSingle={false} />
            <Chip
              label={`$${board.pricePerSquare}/square`}
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Player count and open squares */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PeopleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {totalPlayerCount} player{totalPlayerCount !== 1 ? 's' : ''}
              </Typography>
            </Box>
            {openSquares !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <GridIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {openSquares} open
                </Typography>
              </Box>
            )}
          </Box>

          {/* Footer: Owner info */}
          {board.owner && (
            <>
              <Divider sx={{ mt: 2, mb: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlayerAvatar
                    displayName={board.owner.displayName}
                    handle={board.owner.handle}
                    iconName={board.owner.iconName}
                    size="small"
                    showBadge={false}
                  />
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>
                    {board.owner.displayName}
                  </Typography>
                </Box>
                <Chip
                  label={board.isOwner ? 'Your Board' : 'Joined'}
                  size="small"
                  variant={board.isOwner ? 'filled' : 'outlined'}
                  color={board.isOwner ? 'primary' : 'default'}
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              </Box>
            </>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
